import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import {
  ChannelNotificationPreference,
  StoryNotificationPreference,
  useChannelsPreviewNotificationPreferencesQuery,
  useStoryUpdateSubscriptionStatusMutation,
} from '../../../services';
import { useAppDispatch } from '../../../shared';
import { channelNotificationPreferenceUpdate } from '../../../slices';
import { ChannelNotificationChange, NotificationStoryChange } from './ChannelPreferences';

export const useChannelNotificationPreferences = () => {
  const { t } = useTranslation();

  const dispatch = useAppDispatch();

  const { data: fetchedChannels, isLoading } = useChannelsPreviewNotificationPreferencesQuery();

  const [updateStoryStatus] = useStoryUpdateSubscriptionStatusMutation();

  const [channelOverrideById, setChannelOverrideById] = useState<Record<number, boolean>>({});

  const [storyOverrideById, setStoryOverrideById] = useState<Record<number, boolean>>({});

  const isChannelEnabled = useCallback(
    ({
      id,
      userNotificationPreference: { allowToReceiveChannelNotification },
    }: ChannelNotificationPreference) =>
      channelOverrideById[id] ?? allowToReceiveChannelNotification,
    [channelOverrideById]
  );

  const isStoryEnabled = useCallback(
    ({ id, subscriptionStatus }: StoryNotificationPreference) =>
      storyOverrideById[id] ?? subscriptionStatus,
    [storyOverrideById]
  );

  const setChannelEnabled = useCallback(
    async ({ channelId, isEnabled }: ChannelNotificationChange) => {
      setChannelOverrideById((channelOverrideByIdPrev) => ({
        ...channelOverrideByIdPrev,
        [channelId]: isEnabled,
      }));

      const revertChannel = () => {
        setChannelOverrideById((channelOverrideByIdPrev) => ({
          ...channelOverrideByIdPrev,
          [channelId]: !isEnabled,
        }));

        toast.error(t('common.error-message'));
      };

      try {
        const { updated } = await dispatch(
          channelNotificationPreferenceUpdate({
            channelId,
            fields: { allowToReceiveChannelNotification: isEnabled },
          })
        ).unwrap();

        if (!updated) {
          revertChannel();
        }
      } catch (_) {
        revertChannel();
      }
    },
    [dispatch, t]
  );

  const setStoryEnabled = useCallback(
    async ({ storyId, isEnabled }: NotificationStoryChange) => {
      setStoryOverrideById((storyOverrideByIdPrev) => ({
        ...storyOverrideByIdPrev,
        [storyId]: isEnabled,
      }));

      const revertStory = () => {
        setStoryOverrideById((storyOverrideByIdPrev) => ({
          ...storyOverrideByIdPrev,
          [storyId]: !isEnabled,
        }));

        toast.error(t('common.error-message'));
      };

      try {
        const isUpdated = await updateStoryStatus({ storyId, status: isEnabled }).unwrap();

        if (!isUpdated) {
          revertStory();
        }
      } catch (_) {
        revertStory();
      }
    },
    [t, updateStoryStatus]
  );

  return useMemo(
    () => ({
      channels: fetchedChannels ?? [],
      isLoadingChannels: isLoading,
      isChannelEnabled,
      isStoryEnabled,
      setChannelEnabled,
      setStoryEnabled,
    }),
    [
      fetchedChannels,
      isChannelEnabled,
      isLoading,
      isStoryEnabled,
      setChannelEnabled,
      setStoryEnabled,
    ]
  );
};
